import React, { useState, useEffect } from 'react';
import { Star, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';

interface RatingModalProps {
  advisorId: string;
  onClose: () => void;
  onRatingSubmit: () => void;
}

export default function RatingModal({ advisorId, onClose, onRatingSubmit }: RatingModalProps) {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasExistingReview, setHasExistingReview] = useState(false);
  const [existingReviewMessage, setExistingReviewMessage] = useState<string | null>(null);
  const [existingRating, setExistingRating] = useState(0);
  const [existingFeedback, setExistingFeedback] = useState('');
  const [modalVisible, setModalVisible] = useState(true);

  useEffect(() => {
    // Check if user has already left a review for this advisor
    const checkExistingReview = async () => {
      if (!user || !advisorId) return;
      
      try {
        const { data, error } = await supabase
          .from('advisor_ratings')
          .select('id, rating, feedback')
          .eq('advisor_id', advisorId)
          .eq('client_id', user.id)
          .maybeSingle();

        if (error) throw error;
        
        if (data) {
          setHasExistingReview(true);
          setExistingReviewMessage(`You've already rated this advisor ${data.rating} stars.`);
          setExistingRating(data.rating);
          setExistingFeedback(data.feedback || '');
          setRating(data.rating);
          setFeedback(data.feedback || '');
        }
      } catch (err) {
        console.error('Error checking existing review:', err);
      }
    };

    checkExistingReview();
  }, [user, advisorId, onClose]);

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (!user) {
        throw new Error('You must be logged in to submit a rating');
      }
      
      console.log('Submitting rating for advisor:', advisorId);
      
      // Get current advisor stats using maybeSingle() to handle no results
      const { data: advisor, error: advisorError } = await supabase
        .from('profiles')
        .select('rating, total_readings')
        .eq('id', advisorId)
        .maybeSingle();

      if (advisorError) throw advisorError;
      if (!advisor) throw new Error('Advisor not found');

      // Calculate new rating
      const currentRating = advisor.rating || 0;
      const totalReadings = advisor.total_readings || 0;
      const newTotalReadings = totalReadings + (hasExistingReview ? 0 : 1);
      
      // If updating an existing review, adjust the calculation
      let newRating;
      if (hasExistingReview) {
        // Remove old rating and add new one
        newRating = ((currentRating * totalReadings) - existingRating + rating) / totalReadings;
      } else {
        // Add new rating
        newRating = ((currentRating * totalReadings) + rating) / newTotalReadings;
      }

      console.log('Updating advisor profile with new rating:', newRating);
      
      // Update advisor profile with new rating
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          rating: newRating,
          total_readings: newTotalReadings,
          updated_at: new Date().toISOString()
        })
        .eq('id', advisorId);

      if (updateError) throw updateError;

      console.log('Storing rating details in advisor_ratings table');
      
      // Store rating details - upsert to handle both new and updated reviews
      const { error: ratingError } = await supabase
        .from('advisor_ratings')
        .upsert({
          advisor_id: advisorId,
          client_id: user.id,
          rating,
          feedback,
          created_at: new Date().toISOString()
        }, {
          onConflict: 'advisor_id,client_id'
        });

      if (ratingError) throw ratingError;

      console.log('Rating submitted successfully, calling onRatingSubmit');
      
      // Show success state briefly before closing
      setSubmitting(false);
      setHasExistingReview(true);
      setExistingReviewMessage('Thank you for your rating!');
      
      // Wait a moment before closing to show success state
      setTimeout(() => {
        onRatingSubmit();
      }, 1500);
      
    } catch (err) {
      console.error('Error submitting rating:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit rating. Please try again.';
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  // If modal is not visible, don't show it
  if (!modalVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Rate Your Reading</h2>
          <button
            onClick={() => {
              setModalVisible(false);
              setTimeout(onClose, 100);
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {hasExistingReview && existingReviewMessage && !submitting ? (
          <div className="mb-6 p-4 bg-purple-50 rounded-lg text-purple-700">
            <p className="text-center w-full">{existingReviewMessage}</p>
            <p className="text-center text-sm mt-2">You can update your review below.</p>
          </div>
        ) : null}

        {/* Star Rating */}
        <div className="flex justify-center space-x-2 mb-6">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              onMouseEnter={() => setHoveredRating(value)}
              onMouseLeave={() => setHoveredRating(0)}
              onClick={() => setRating(value)}
              className="focus:outline-none"
            >
              <Star
                className={`w-8 h-8 ${
                  (hoveredRating || rating) >= value
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-gray-300'
                } transition-colors`}
              />
            </button>
          ))}
        </div>

        {/* Feedback Text Area */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Feedback (Optional)
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-purple-500 focus:border-purple-500"
            rows={4}
            placeholder="Share your experience with this advisor..."
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting...' : hasExistingReview ? 'Update Rating' : 'Submit Rating'}
        </button>
      </div>
    </div>
  );
}